import React, { useMemo, useState } from 'react';
import { Badge, Button, Checkbox, Drawer, Input, Textarea, useToast } from '../../ui/components';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';

function initialSelected(role, catalog) {
  if (!role) return new Set();
  if (role.permissions?.includes('*')) {
    return new Set(catalog.flatMap((g) => g.permissions.map((p) => p.key)));
  }
  return new Set(role.permissions ?? []);
}

export default function RoleEditorDrawer({ open, role, catalog, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const isSystem = !!role?.isSystem;
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState(() => initialSelected(role, catalog));
  const [saving, setSaving] = useState(false);

  const allKeys = useMemo(() => catalog.flatMap((g) => g.permissions.map((p) => p.key)), [catalog]);

  const toggle = (key) => {
    if (isSystem) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleGroup = (group, on) => {
    if (isSystem) return;
    setSelected((prev) => {
      const next = new Set(prev);
      group.permissions.forEach((p) => (on ? next.add(p.key) : next.delete(p.key)));
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isSystem) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), permissions: allKeys.filter((k) => selected.has(k)) };
      const saved = role
        ? await usersApi.updateRole(role.id, { id: role.id, ...payload })
        : await usersApi.createRole(payload);
      toast.success(role ? t('Role updated') : t('Role created'));
      onSaved?.(saved);
    } catch (err) {
      toast.error(t('Could not save role'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const title = role ? (isSystem ? t('{name} (built-in)', { name: role.name }) : t('Edit role')) : t('New role');

  return (
    <Drawer open={open} onClose={() => (saving ? null : onClose())} title={title} size="lg">
      <form className="set-form" onSubmit={submit}>
        {isSystem && (
          <p className="set-section__hint">{t('Built-in roles are read-only. Create a custom role to tailor permissions.')}</p>
        )}
        <Input label={t('Role name')} value={name} onChange={(e) => setName(e.target.value)} disabled={isSystem} autoFocus={!isSystem} />
        <Textarea label={t('Description')} value={description} onChange={(e) => setDescription(e.target.value)} disabled={isSystem} rows={2} />

        <div className="usr-perms">
          {catalog.map((group) => {
            const total = group.permissions.length;
            const on = group.permissions.filter((p) => selected.has(p.key)).length;
            return (
              <div className="usr-perm-group" key={group.key}>
                <div className="usr-perm-group__head">
                  <Checkbox
                    checked={on === total && total > 0}
                    indeterminate={on > 0 && on < total}
                    onChange={(e) => toggleGroup(group, e.target.checked)}
                    disabled={isSystem}
                    label={group.label}
                  />
                  <Badge tone="neutral">{on}/{total}</Badge>
                </div>
                <div className="usr-perm-list">
                  {group.permissions.map((p) => (
                    <Checkbox
                      key={p.key}
                      checked={selected.has(p.key)}
                      onChange={() => toggle(p.key)}
                      disabled={isSystem}
                      label={p.label}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="set-form__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>{t('Close')}</Button>
          {!isSystem && <Button type="submit" variant="primary" loading={saving}>{role ? t('Save changes') : t('Create role')}</Button>}
        </div>
      </form>
    </Drawer>
  );
}
